import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Button,
  TextInput,
  Table,
  Stack,
  Box,
  Loader,
  Center,
  Badge,
  ActionIcon,
  Menu,
  Select,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  Plus,
  Search,
  MoreVertical,
  Download,
  ExternalLink,
  Edit,
  Trash2,
  File,
  Link as LinkIcon,
  FileText,
  Image,
  Film,
  Info,
  DownloadCloud,
} from 'react-feather';
import type { ResourceWithDetails, ResourceCategory } from '../../../types';
import { resourcesApi } from '../../../services/api/resources';
import { formatDate } from '../../../utils/formatters';
import { AddEvergreenResourceModal } from './AddEvergreenResourceModal';
import { EditEvergreenResourceModal } from './EditEvergreenResourceModal';
import { ExportModal, EmptyState } from '../../../components/common';
import classes from './EvergreenResourcesPage.module.css';

// Map MIME types to icons
function getResourceIcon(resource: ResourceWithDetails) {
  if (resource.resource_type === 'link') {
    return <LinkIcon size={16} />;
  }

  const mimeType = resource.mime_type || '';

  if (mimeType.startsWith('image/')) {
    return <Image size={16} />;
  }
  if (mimeType.startsWith('video/')) {
    return <Film size={16} />;
  }
  if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word')) {
    return <FileText size={16} />;
  }
  return <File size={16} />;
}

// Format file size for display
function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function EvergreenResourcesPage() {
  const [resources, setResources] = useState<ResourceWithDetails[]>([]);
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Modal state
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] = useDisclosure(false);
  const [selectedResource, setSelectedResource] = useState<ResourceWithDetails | null>(null);

  // Fetch categories on mount
  useEffect(() => {
    resourcesApi.getCategories().then((cats) => {
      setCategories(cats);
    }).catch((error) => {
      console.error('Failed to fetch categories:', error);
    });
  }, []);

  // Fetch evergreen resources
  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      const result = await resourcesApi.getEvergreenResources({
        search: debouncedSearch || undefined,
        category_id: categoryFilter || undefined,
      });
      setResources(result.data);
      setTotalCount(result.pagination.total);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to fetch resources',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, categoryFilter]);

  // Fetch resources when filters change
  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // Handle edit
  const handleEdit = (resource: ResourceWithDetails) => {
    setSelectedResource(resource);
    openEditModal();
  };

  // Handle delete
  const handleDelete = (resource: ResourceWithDetails) => {
    modals.openConfirmModal({
      title: 'Delete Evergreen Resource',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{resource.title}</strong>? This resource will no
          longer be available to any cohorts.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await resourcesApi.delete(resource.id);
          notifications.show({
            title: 'Success',
            message: 'Resource deleted successfully',
            color: 'green',
          });
          fetchResources();
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to delete resource',
            color: 'red',
          });
        }
      },
    });
  };

  // Handle download/open
  const handleOpen = (resource: ResourceWithDetails) => {
    if (resource.resource_type === 'link' && resource.url) {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    } else if (resource.download_url) {
      window.open(resource.download_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Handle resource created
  const handleResourceCreated = () => {
    closeAddModal();
    fetchResources();
    notifications.show({
      title: 'Success',
      message: 'Evergreen resource created successfully',
      color: 'green',
    });
  };

  // Handle resource updated
  const handleResourceUpdated = () => {
    closeEditModal();
    setSelectedResource(null);
    fetchResources();
    notifications.show({
      title: 'Success',
      message: 'Resource updated successfully',
      color: 'green',
    });
  };

  // Category options for filter
  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  // Empty state (no resources at all)
  const hasFilters = debouncedSearch || categoryFilter;
  if (!loading && resources.length === 0 && !hasFilters) {
    return (
      <Container size="xl" py="xl">
        <Title order={1} mb="xl">
          Evergreen Resources
        </Title>
        <Alert icon={<Info size={16} />} color="teal" mb="lg">
          Evergreen resources are global resources that are automatically available to all cohorts.
          Cohort managers can hide specific evergreen resources from their cohorts if needed.
        </Alert>
        <EmptyState
          icon={<File size={40} strokeWidth={1.5} />}
          title="No evergreen resources yet"
          description="Get started by adding your first evergreen resource that will be available across all cohorts."
          action={
            <Button leftSection={<Plus size={18} />} onClick={openAddModal}>
              Add Evergreen Resource
            </Button>
          }
          size="lg"
          withCard
        />

        <AddEvergreenResourceModal
          opened={addModalOpened}
          onClose={closeAddModal}
          categories={categories}
          onCreated={handleResourceCreated}
        />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Box>
          <Title order={1}>Evergreen Resources</Title>
          <Text c="dimmed" size="sm" mt={4}>
            {totalCount} {totalCount === 1 ? 'resource' : 'resources'} available to all cohorts
          </Text>
        </Box>
        <Group gap="sm">
          <Button variant="default" leftSection={<DownloadCloud size={18} />} onClick={openExportModal}>
            Export
          </Button>
          <Button leftSection={<Plus size={18} />} onClick={openAddModal}>
            Add Evergreen Resource
          </Button>
        </Group>
      </Group>

      {/* Info banner */}
      <Alert icon={<Info size={16} />} color="teal" mb="lg">
        Evergreen resources are global resources that are automatically available to all cohorts.
        Cohort managers can hide specific evergreen resources from their cohorts if needed.
      </Alert>

      {/* Search and Filter Panel */}
      <Card withBorder p="md" mb="lg" className={classes.filterPanel} styles={{ root: { boxShadow: 'none' } }}>
        <Group gap="md">
          <TextInput
            placeholder="Search resources..."
            leftSection={<Search size={16} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            style={{ flex: 1, maxWidth: 300 }}
          />
          <Select
            placeholder="All categories"
            data={categoryOptions}
            value={categoryFilter}
            onChange={setCategoryFilter}
            clearable
            style={{ width: 180 }}
          />
        </Group>
      </Card>

      {/* Content */}
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <Card withBorder p={0} styles={{ root: { boxShadow: 'none' } }}>
          <Table.ScrollContainer minWidth={800}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 40 }}></Table.Th>
                  <Table.Th>
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                      Title
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                      Category
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                      Access
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                      Added
                    </Text>
                  </Table.Th>
                  <Table.Th style={{ width: 80 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {resources.map((resource) => (
                  <Table.Tr key={resource.id} className={classes.tableRow}>
                    {/* Icon */}
                    <Table.Td>
                      <Group gap={4}>{getResourceIcon(resource)}</Group>
                    </Table.Td>

                    {/* Title & description */}
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap="xs">
                          <Text fw={500} size="sm">
                            {resource.title}
                          </Text>
                          <Badge size="xs" color="teal" variant="light">
                            EVERGREEN
                          </Badge>
                        </Group>
                        {resource.description && (
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {resource.description}
                          </Text>
                        )}
                        {resource.resource_type === 'file' && resource.file_size && (
                          <Text size="xs" c="dimmed">
                            {resource.original_filename} ({formatFileSize(resource.file_size)})
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>

                    {/* Category */}
                    <Table.Td>
                      {resource.category_name ? (
                        <Badge variant="light" color="gray" size="sm">
                          {resource.category_name}
                        </Badge>
                      ) : (
                        <Text c="dimmed" size="sm">
                          —
                        </Text>
                      )}
                    </Table.Td>

                    {/* Access badges */}
                    <Table.Td>
                      <Group gap={4}>
                        {resource.access_coaches && (
                          <Badge size="xs" color="blue" variant="light">
                            COACHES
                          </Badge>
                        )}
                        {resource.access_participants && (
                          <Badge size="xs" color="green" variant="light">
                            PARTICIPANTS
                          </Badge>
                        )}
                        {resource.access_partners && (
                          <Badge size="xs" color="purple" variant="light">
                            PARTNERS
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>

                    {/* Created date */}
                    <Table.Td>
                      <Text size="sm">{formatDate(resource.created_at)}</Text>
                    </Table.Td>

                    {/* Actions */}
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        {/* Open/Download */}
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => handleOpen(resource)}
                        >
                          {resource.resource_type === 'link' ? (
                            <ExternalLink size={16} />
                          ) : (
                            <Download size={16} />
                          )}
                        </ActionIcon>

                        {/* More actions menu */}
                        <Menu shadow="md" width={160} position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                              <MoreVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<Edit size={14} />}
                              onClick={() => handleEdit(resource)}
                            >
                              Edit
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<Trash2 size={14} />}
                              color="red"
                              onClick={() => handleDelete(resource)}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {resources.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text ta="center" c="dimmed" py="xl">
                        No resources found matching your filters
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      {/* Add Evergreen Resource Modal */}
      <AddEvergreenResourceModal
        opened={addModalOpened}
        onClose={closeAddModal}
        categories={categories}
        onCreated={handleResourceCreated}
      />

      {/* Edit Resource Modal */}
      {selectedResource && (
        <EditEvergreenResourceModal
          opened={editModalOpened}
          onClose={() => {
            closeEditModal();
            setSelectedResource(null);
          }}
          resource={selectedResource}
          categories={categories}
          onUpdated={handleResourceUpdated}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        entityType="resources"
        recordCount={totalCount}
        filterContext={{
          search: debouncedSearch || undefined,
          category_id: categoryFilter || undefined,
          is_evergreen: true,
        }}
        filterSummary={[
          ...(debouncedSearch ? [{ label: 'Search', value: debouncedSearch }] : []),
          ...(categoryFilter ? [{ label: 'Category', value: categories.find(c => c.id === categoryFilter)?.name || categoryFilter }] : []),
        ]}
      />
    </Container>
  );
}

export default EvergreenResourcesPage;
